import React, { useEffect, useState } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { Check, AlertCircle } from "lucide-react";
import { screen1Icons } from "./svAssets";

const GENDER_LABEL = { M: "Male", F: "Female", O: "Others" };

export const getAge = (dob) => (dob && dob.isValid() ? dayjs().diff(dob, "year") : null);

const yesNo = (v) => (v === true ? "Yes" : v === false ? "No" : "—");

// Same ranges the old form enforced.
export const validateDetails = (d) => {
  const errors = {};
  if (!d.dob || !d.dob.isValid()) errors.dob = "Select your date of birth";
  if (!d.gender) errors.gender = "Select gender";
  const h = Number(d.height);
  if (!d.height) errors.height = "Enter height";
  else if (!/^\d+$/.test(String(d.height)) || h < 90 || h > 250) errors.height = "Height must be between 90 and 250 cm";
  const w = Number(d.weight);
  if (!d.weight) errors.weight = "Enter weight";
  else if (!/^\d+$/.test(String(d.weight)) || w < 20 || w > 300) errors.weight = "Weight must be between 20 and 300 kg";
  if (typeof d.diabetes !== "boolean") errors.diabetes = "Select an option";
  if (typeof d.smoke !== "boolean") errors.smoke = "Select an option";
  return errors;
};

const Segmented = ({ value, options, onChange }) => (
  <div className="sv-seg">
    {options.map((o) => (
      <button
        key={String(o.value)}
        type="button"
        className={value === o.value ? "is-on" : ""}
        aria-pressed={value === o.value}
        onClick={() => onChange(o.value)}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const Field = ({ label, error, children }) => (
  <div className={`sv-field${error ? " has-error" : ""}`}>
    <label>{label}</label>
    {children}
    {error && <p className="sv-error">{error}</p>}
  </div>
);

const formatReportDate = (value) => {
  const d = dayjs(value);
  return d.isValid() ? d.format("DD MMM YYYY, hh:mm a") : "";
};

export default function PatientDetailsCard({
  details,
  loading,
  isEditing,
  prefilled,
  edited,
  lastReport,
  lastReportState,
  onEdit,
  onCancel,
  onSave,
  onViewReport,
}) {
  const [draft, setDraft] = useState(details);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEditing) {
      setDraft(details);
      setErrors({});
    }
  }, [isEditing, details]);

  const fullName = `${details.firstName || ""} ${details.lastName || ""}`.trim() || "—";
  const initials = fullName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");

  const set = (key) => (value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSave = () => {
    const next = validateDetails(draft);
    setErrors(next);
    if (Object.keys(next).length === 0) onSave(draft);
  };

  if (loading) {
    return (
      <div className="sv-card sv-patient">
        <div className="sv-patient-head">
          <div className="sv-skel" style={{ width: 40, height: 40, borderRadius: "50%" }} />
          <div style={{ flex: 1 }}>
            <div className="sv-skel" style={{ height: 14, width: "70%", marginBottom: 6 }} />
            <div className="sv-skel" style={{ height: 10, width: "50%" }} />
          </div>
        </div>
        <div className="sv-facts">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="sv-skel" style={{ height: 52 }} />
          ))}
        </div>
      </div>
    );
  }

  const incomplete = Object.keys(validateDetails(details)).length > 0;
  const age = getAge(details.dob);

  return (
    <div className="sv-card sv-patient">
      <div className="sv-patient-head">
        <div className="sv-avatar">{initials || "?"}</div>
        <div style={{ minWidth: 0 }}>
          <p className="sv-patient-name">{fullName}</p>
          {details.patientId && <p className="sv-patient-id">Patient ID {details.patientId}</p>}
        </div>
        {!isEditing && (
          <button type="button" className="sv-btn-outline" onClick={onEdit}>
            <img src={screen1Icons.edit} alt="" width={13} height={13} /> Edit
          </button>
        )}
      </div>

      {isEditing ? (
        <>
          <div className="sv-form">
            <Field label="Date of birth" error={errors.dob}>
              <DatePicker
                value={draft.dob}
                onChange={set("dob")}
                format="DD MMM YYYY"
                placeholder="Select date"
                allowClear={false}
                disabledDate={(d) => d && d.isAfter(dayjs())}
                getPopupContainer={(t) => t.parentNode}
              />
            </Field>
            <Field label="Gender" error={errors.gender}>
              <Segmented
                value={draft.gender}
                onChange={set("gender")}
                options={[
                  { value: "F", label: "Female" },
                  { value: "M", label: "Male" },
                ]}
              />
            </Field>
            <Field label="Height (cm)" error={errors.height}>
              <input
                className="sv-input"
                inputMode="numeric"
                value={draft.height}
                maxLength={3}
                onChange={(e) => set("height")(e.target.value.replace(/\D/g, "").slice(0, 3))}
                placeholder="e.g. 160"
              />
            </Field>
            <Field label="Weight (kg)" error={errors.weight}>
              <input
                className="sv-input"
                inputMode="numeric"
                value={draft.weight}
                maxLength={3}
                onChange={(e) => set("weight")(e.target.value.replace(/\D/g, "").slice(0, 3))}
                placeholder="e.g. 65"
              />
            </Field>
            <Field label="Diabetes" error={errors.diabetes}>
              <Segmented
                value={draft.diabetes}
                onChange={set("diabetes")}
                options={[
                  { value: false, label: "No" },
                  { value: true, label: "Yes" },
                ]}
              />
            </Field>
            <Field label="Smoke" error={errors.smoke}>
              <Segmented
                value={draft.smoke}
                onChange={set("smoke")}
                options={[
                  { value: false, label: "No" },
                  { value: true, label: "Yes" },
                ]}
              />
            </Field>
          </div>
          <p className="sv-form-hint">Your name comes from your CureBay account and can't be changed here.</p>
          <div className="sv-form-actions">
            <button type="button" className="sv-btn sv-btn-primary" onClick={handleSave}>
              Save details
            </button>
            {!incomplete && (
              <button type="button" className="sv-btn sv-btn-ghost" onClick={onCancel}>
                Cancel
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="sv-facts">
            <div className="sv-fact">
              <p className="sv-fact-label">Age</p>
              <p className="sv-fact-value">{age != null ? `${age} yrs` : "—"}</p>
            </div>
            <div className="sv-fact">
              <p className="sv-fact-label">Gender</p>
              <p className="sv-fact-value">{GENDER_LABEL[details.gender] || "—"}</p>
            </div>
            <div className="sv-fact">
              <p className="sv-fact-label">Height</p>
              <p className="sv-fact-value">{details.height ? `${details.height} cm` : "—"}</p>
            </div>
            <div className="sv-fact">
              <p className="sv-fact-label">Weight</p>
              <p className="sv-fact-value">{details.weight ? `${details.weight} kg` : "—"}</p>
            </div>
            <div className="sv-fact">
              <p className="sv-fact-label">Diabetes</p>
              <p className="sv-fact-value">{yesNo(details.diabetes)}</p>
            </div>
            <div className="sv-fact">
              <p className="sv-fact-label">Smoke</p>
              <p className="sv-fact-value">{yesNo(details.smoke)}</p>
            </div>
          </div>

          {incomplete ? (
            <div className="sv-note is-warn">
              <AlertCircle size={13} /> A few details are missing. Tap Edit to add them.
            </div>
          ) : edited ? (
            <div className="sv-note">
              <Check size={13} /> Updated. We'll save this with your check.
            </div>
          ) : prefilled ? (
            <div className="sv-note">
              <Check size={13} /> Saved from your last check. Nothing to fill in.
            </div>
          ) : null}
        </>
      )}

      {lastReport && (
        <div className="sv-last">
          <img className="sv-last-icon" src={screen1Icons.lastReport} alt="" />
          <div>
            <p className="sv-last-label">Last report</p>
            <p className="sv-last-date">{formatReportDate(lastReport.CREATEDDATE || lastReport.created_date)}</p>
          </div>
          {lastReportState === 'ready' ? (
            <button type="button" className="sv-btn-outline" onClick={onViewReport}>
              View
            </button>
          ) : (
            <span className={`sv-pill${lastReportState === 'failed' ? ' is-warn' : ' is-muted'}`} style={{ marginLeft: 'auto' }}>
              {lastReportState === 'failed' ? 'Failed' : 'Processing'}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
